import { Dialog, IconButton, OutlinedInput, styled } from '@mui/material';
import { END_HOUR, HOUR_HEIGHT, START_HOUR } from './agenda';

export const HomeShell = styled('section')(({ theme }) => ({
  borderRadius: 18,
  minHeight: 'calc(100vh - 48px)',
}));

export const AgendaCard = styled('div')(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.96)',
  border: '1px solid rgba(255, 255, 255, 0.72)',
  borderRadius: 14,
  boxShadow: '0 28px 70px rgba(36, 62, 92, 0.16)',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  minHeight: 620,
  overflow: 'hidden',
  padding: theme.spacing(2),
}));

export const AgendaTopbar = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'grid',
  gap: theme.spacing(2),
  gridTemplateColumns: 'minmax(210px, 1fr) minmax(220px, 360px) auto',

  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: '1fr',
  },
}));

export const BrandArea = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.spacing(1.25),
  minWidth: 0,

  '& .MuiTypography-overline': {
    color: theme.palette.primary.main,
    fontWeight: 800,
    letterSpacing: 0,
  },

  '& .MuiTypography-h5': {
    fontWeight: 900,
    textTransform: 'capitalize',
  },
}));

export const BrandBadge = styled('div')(({ theme }) => ({
  alignItems: 'center',
  background: theme.palette.primary.main,
  borderRadius: 10,
  color: theme.palette.primary.contrastText,
  display: 'flex',
  height: 42,
  justifyContent: 'center',
  width: 42,
}));

export const SearchInput = styled(OutlinedInput)(({ theme }) => ({
  background: '#fff',
  borderRadius: 8,
  height: 40,

  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.divider,
  },
}));

export const TopbarActions = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.spacing(1),
  justifyContent: 'flex-end',

  [theme.breakpoints.down('lg')]: {
    justifyContent: 'flex-start',
  },
}));

export const WeekPill = styled('span')(({ theme }) => ({
  background: 'rgba(132, 20, 73, 0.08)',
  border: `1px solid ${theme.palette.primary.light}`,
  borderRadius: 999,
  color: theme.palette.primary.main,
  fontSize: 12,
  fontWeight: 800,
  padding: theme.spacing(0.75, 1.5),
}));

export const SummaryStrip = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(1),
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',

  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
}));

export const SummaryItem = styled('div')(({ theme }) => ({
  background: '#f8fafc',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 10,
  padding: theme.spacing(1.25, 1.5),

  '& .MuiTypography-caption': {
    color: theme.palette.text.secondary,
    fontWeight: 700,
  },

  '& .MuiTypography-h6': {
    color: theme.palette.text.primary,
    fontWeight: 900,
  },
}));

export const CalendarBoard = styled('div')(({ theme }) => ({
  background: '#fff',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  overflowX: 'auto',
  overflowY: 'hidden',
}));

export const CalendarHeader = styled('div')(({ theme }) => ({
  background: '#f8fafc',
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'grid',
  gridTemplateColumns: '72px repeat(7, minmax(130px, 1fr))',
  minWidth: 1040,
}));

export const CalendarCorner = styled('div')(({ theme }) => ({
  alignItems: 'center',
  borderRight: `1px solid ${theme.palette.divider}`,
  color: theme.palette.primary.main,
  display: 'flex',
  justifyContent: 'center',
}));

export const DayHeader = styled('div')<{ istoday: number }>(({ istoday, theme }) => ({
  background: istoday ? 'rgba(132, 20, 73, 0.07)' : 'transparent',
  borderRight: `1px solid ${theme.palette.divider}`,
  minHeight: 46,
  padding: theme.spacing(0.7, 1),
  textAlign: 'center',

  '& .MuiTypography-caption': {
    color: istoday ? theme.palette.primary.main : theme.palette.text.secondary,
    display: 'block',
    fontWeight: 800,
    textTransform: 'capitalize',
  },

  '& .MuiTypography-body2': {
    color: theme.palette.text.primary,
    fontWeight: istoday ? 900 : 700,
  },
}));

export const CalendarBody = styled('div')({
  display: 'grid',
  gridTemplateColumns: '72px minmax(968px, 1fr)',
  minWidth: 1040,
  position: 'relative',
});

export const TimeColumn = styled('div')(({ theme }) => ({
  background: '#fff',
  borderRight: `1px solid ${theme.palette.divider}`,
  display: 'grid',
  gridTemplateRows: `repeat(${END_HOUR - START_HOUR + 1}, ${HOUR_HEIGHT}px)`,
  minHeight: (END_HOUR - START_HOUR) * HOUR_HEIGHT,
}));

export const TimeMark = styled('div')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 11,
  padding: theme.spacing(0.35, 1),
  textAlign: 'right',
}));

export const DaysGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, minmax(130px, 1fr))',
  minHeight: (END_HOUR - START_HOUR) * HOUR_HEIGHT,
  position: 'relative',
});

export const DayColumn = styled('div')(({ theme }) => ({
  borderRight: `1px solid ${theme.palette.divider}`,
  minHeight: (END_HOUR - START_HOUR) * HOUR_HEIGHT,
  position: 'relative',
}));

export const HourLine = styled('div')(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  height: HOUR_HEIGHT,
}));

export const LoadingColumn = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  left: theme.spacing(1),
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
}));

export const AppointmentBlock = styled('button', {
  shouldForwardProp: prop => prop !== 'tone' && prop !== 'isselected',
})<{ tone: string; isselected: number }>(({ isselected, theme, tone }) => {
  const colors: Record<string, { bg: string; border: string; text: string }> = {
    blue: { bg: '#bdefff', border: '#f4ffff', text: '#07526e' },
    green: { bg: '#bff7d1', border: '#f4fff8', text: '#0f6330' },
    pink: { bg: '#ffd2dc', border: '#fff5f7', text: '#8a1230' },
    purple: { bg: '#ded6ff', border: '#f7f4ff', text: '#4730a1' },
    yellow: { bg: '#fff0ad', border: '#fff8da', text: '#7a5400' },
  };
  const selectedColors = colors[tone] || colors.blue;

  return {
    background: selectedColors.bg,
    border: `1px solid ${selectedColors.border}`,
    borderRadius: 5,
    boxShadow: isselected ? '0 10px 22px rgba(0, 0, 0, 0.24)' : '0 4px 10px rgba(0, 0, 0, 0.16)',
    color: selectedColors.text,
    cursor: 'pointer',
    display: 'grid',
    gap: theme.spacing(0.5),
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    left: theme.spacing(1),
    minWidth: 0,
    overflow: 'hidden',
    padding: theme.spacing(0.35, 0.7),
    position: 'absolute',
    right: theme.spacing(1),
    textAlign: 'left',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    zIndex: isselected ? 3 : 2,

    '&:hover': {
      transform: 'translateY(-2px)',
    },

    '& .MuiTypography-body2': {
      color: selectedColors.text,
      fontSize: 11,
      fontWeight: 800,
      lineHeight: 1.15,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  };
});

export const AppointmentMeta = styled('span')({
  color: 'inherit',
  fontSize: 10,
  fontWeight: 900,
  lineHeight: 1.15,
});

export const GuideDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 14,
    boxShadow: '0 28px 70px rgba(15, 23, 42, 0.28)',
  },

  '& .MuiDialogTitle-root': {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1.5, 2),
  },

  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },

  '& .MuiDialogActions-root': {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1.25, 2),
  },
}));

export const DetailsHeader = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'grid',
  gap: theme.spacing(1),
  gridTemplateColumns: 'auto minmax(0, 1fr) auto auto',

  '& .MuiAvatar-root': {
    backgroundColor: theme.palette.primary.main,
    fontSize: 12,
    fontWeight: 900,
    height: 36,
    width: 36,
  },

  '& .MuiTypography-subtitle2': {
    fontWeight: 900,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

export const CloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginRight: theme.spacing(-0.5),
}));

export const DetailLine = styled('div')(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.text.secondary,
  display: 'flex',
  fontSize: 13,
  gap: theme.spacing(0.75),

  '& svg': {
    color: theme.palette.primary.main,
  },
}));

export const DetailsGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(1.25),
}));

export const DetailValue = styled('div')(({ theme }) => ({
  background: '#f8fafc',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 10,
  padding: theme.spacing(1.25),

  '& .MuiTypography-h6': {
    fontWeight: 900,
  },
}));

export const EmptyOverlay = styled('div')(({ theme }) => ({
  alignItems: 'center',
  background: 'rgba(255, 255, 255, 0.84)',
  borderRadius: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  justifyContent: 'center',
  left: '50%',
  padding: theme.spacing(3),
  position: 'absolute',
  textAlign: 'center',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 5,
}));

export const LoadingState = styled('div')(({ theme }) => ({
  alignItems: 'center',
  background: 'rgba(255, 255, 255, 0.72)',
  display: 'flex',
  gap: theme.spacing(1),
  justifyContent: 'center',
  inset: 0,
  position: 'absolute',
  zIndex: 7,
}));
